import React, { useMemo } from 'react';
import {
  Avatar,
  Box,
  Chip,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import FitScreenIcon from '@mui/icons-material/FitScreen';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import RemoveIcon from '@mui/icons-material/Remove';
import { useTranslation } from 'react-i18next';
import ReactFlow, {
  Background,
  BackgroundVariant,
  ControlButton,
  Controls,
  Edge,
  Handle,
  MarkerType,
  Node,
  NodeProps,
  Position,
  ReactFlowProvider,
  useReactFlow,
} from 'reactflow';
import 'reactflow/dist/style.css';

export interface OrganizationFlowNode {
  id: string;
  name: string;
  title?: string | null;
  department?: string | null;
  avatarUrl?: string | null;
  email?: string | null;
  phone?: string | null;
  status?: string | null;
  isSelf?: boolean;
  directReportCount?: number;
  children: OrganizationFlowNode[];
}

interface OrganizationFlowProps {
  roots: OrganizationFlowNode[];
  search?: string;
  highlightedIds?: Iterable<string>;
  selfLabel?: string;
  statusLabels?: Record<string, string>;
  ariaLabel?: string;
  height?: number;
}

interface OrganizationNodeData extends OrganizationFlowNode {
  highlighted: boolean;
  selfLabel?: string;
  statusLabel?: string;
  hiddenReportCount: number;
  /** Is an edge coming INTO this node — i.e. does it have a manager in this view?
   *  A root has none, and a connection dot with no line attached is decoration
   *  pretending to be information (owner review 2026-08-02). */
  hasParent: boolean;
  /** Is an edge leaving this node — i.e. does it have at least one RENDERED report?
   *  Deliberately not `directReportCount`: reports beyond the rendered set are shown
   *  as the "+N" chip and draw no edge, so they must not summon a dot either. */
  hasChildren: boolean;
}

const NODE_WIDTH = 232;
const HORIZONTAL_GAP = 32;
const VERTICAL_GAP = 132;
const FIT_VIEW_OPTIONS = { padding: 0.2, minZoom: 0.25, maxZoom: 1.25 };

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0][0]?.toUpperCase() ?? '?';
  return `${parts[0][0] ?? ''}${parts[parts.length - 1][0] ?? ''}`.toUpperCase();
}

function matchesSearch(node: OrganizationFlowNode, search: string): boolean {
  const needle = search.trim().toLocaleLowerCase();
  if (!needle) return false;
  return [node.name, node.title, node.department]
    .filter(Boolean)
    .some((value) => value!.toLocaleLowerCase().includes(needle));
}

function subtreeWidth(node: OrganizationFlowNode): number {
  if (node.children.length === 0) return NODE_WIDTH;
  const childrenWidth = node.children.reduce(
    (total, child, index) =>
      total + subtreeWidth(child) + (index === 0 ? 0 : HORIZONTAL_GAP),
    0,
  );
  return Math.max(NODE_WIDTH, childrenWidth);
}

function stopCanvasGesture(event: React.SyntheticEvent) {
  event.stopPropagation();
}

function OrganizationNode({ data, isConnectable }: NodeProps<OrganizationNodeData>) {
  const inactive = Boolean(
    data.status && data.status !== 'active' && data.status !== 'invited',
  );
  const tooltip = (
    <Stack spacing={0.4} sx={{ minWidth: 180, py: 0.25 }}>
      <Typography variant="caption" sx={{ fontWeight: 700 }}>
        {data.name}
      </Typography>
      {data.title && (
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
          {data.title}
        </Typography>
      )}
      {data.department && (
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
          {data.department}
        </Typography>
      )}
      {data.email && (
        <Box
          component="a"
          className="nodrag nopan"
          href={`mailto:${data.email}`}
          aria-label={data.email}
          onMouseDown={stopCanvasGesture}
          onClick={stopCanvasGesture}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            width: 'fit-content',
            color: 'common.white',
            textDecoration: 'none',
            '&:hover': { textDecoration: 'underline' },
          }}
        >
          <EmailOutlinedIcon sx={{ fontSize: 13 }} />
          <Typography variant="caption">{data.email}</Typography>
        </Box>
      )}
      {data.phone && (
        <Box
          component="a"
          className="nodrag nopan"
          href={`tel:${data.phone}`}
          aria-label={data.phone}
          onMouseDown={stopCanvasGesture}
          onClick={stopCanvasGesture}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            width: 'fit-content',
            color: 'common.white',
            textDecoration: 'none',
            '&:hover': { textDecoration: 'underline' },
          }}
        >
          <PhoneOutlinedIcon sx={{ fontSize: 13 }} />
          <Typography variant="caption">{data.phone}</Typography>
        </Box>
      )}
    </Stack>
  );

  return (
    <Tooltip title={tooltip} placement="top" arrow enterDelay={300}>
      <Box
        role="group"
        tabIndex={0}
        sx={{
          width: NODE_WIDTH,
          minHeight: 76,
          px: 1.25,
          py: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          bgcolor: 'background.paper',
          border: data.isSelf ? '2px solid' : '1px solid',
          borderColor: data.highlighted || data.isSelf ? 'primary.main' : 'divider',
          borderRadius: 1,
          opacity: inactive ? 0.68 : 1,
          cursor: 'help',
          transition: 'border-color 120ms ease, background-color 120ms ease',
          ...(data.highlighted && { bgcolor: 'action.hover' }),
          '&:hover, &:focus-visible': { borderColor: 'primary.main', outline: 'none' },
        }}
      >
        {data.hasParent && (
          <Handle
            type="target"
            position={Position.Top}
            isConnectable={isConnectable}
            isConnectableStart={isConnectable}
            isConnectableEnd={isConnectable}
            style={{ width: 5, height: 5, border: 0, background: '#9e9e9e' }}
          />
        )}
        <Avatar
          src={data.avatarUrl ?? undefined}
          sx={{ width: 34, height: 34, fontSize: '0.72rem', bgcolor: 'primary.main' }}
        >
          {initials(data.name)}
        </Avatar>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
            <Typography
              noWrap
              title={data.name}
              sx={{ fontSize: '0.8rem', fontWeight: 600, minWidth: 0 }}
            >
              {data.name}
            </Typography>
            {data.isSelf && data.selfLabel && (
              <Chip
                label={data.selfLabel}
                size="small"
                color="primary"
                sx={{ height: 18, fontSize: '0.62rem', '& .MuiChip-label': { px: 0.5 } }}
              />
            )}
          </Stack>
          <Typography
            noWrap
            title={data.title ?? ''}
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', fontSize: '0.68rem' }}
          >
            {data.title || '—'}
          </Typography>
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.35 }}>
            {data.department && (
              <Chip
                label={data.department}
                size="small"
                variant="outlined"
                sx={{
                  height: 18,
                  maxWidth: 112,
                  fontSize: '0.62rem',
                  borderRadius: 1,
                  '& .MuiChip-label': { px: 0.65, overflow: 'hidden', textOverflow: 'ellipsis' },
                }}
              />
            )}
            {data.statusLabel && (
              <Chip
                label={data.statusLabel}
                size="small"
                variant="outlined"
                sx={{ height: 18, fontSize: '0.62rem', borderRadius: 1, color: 'text.disabled' }}
              />
            )}
            {data.hiddenReportCount > 0 && (
              <Stack direction="row" spacing={0.25} alignItems="center" color="text.disabled">
                <PersonOutlineIcon sx={{ fontSize: 12 }} />
                <Typography variant="caption" sx={{ fontSize: '0.62rem' }}>
                  +{data.hiddenReportCount}
                </Typography>
              </Stack>
            )}
          </Stack>
        </Box>
        {data.hasChildren && (
          <Handle
            type="source"
            position={Position.Bottom}
            isConnectable={isConnectable}
            isConnectableStart={isConnectable}
            isConnectableEnd={isConnectable}
            style={{ width: 5, height: 5, border: 0, background: '#9e9e9e' }}
          />
        )}
      </Box>
    </Tooltip>
  );
}

const nodeTypes = { organizationNode: OrganizationNode };

function OrganizationFlowControls() {
  const { t } = useTranslation();
  const { fitView, zoomIn, zoomOut } = useReactFlow();
  const zoomInLabel = t('common:actions.zoomIn');
  const zoomOutLabel = t('common:actions.zoomOut');
  const fitViewLabel = t('common:actions.fitView');

  return (
    <Controls
      showZoom={false}
      showFitView={false}
      showInteractive={false}
      style={{
        border: '1px solid #e0e0e0',
        borderRadius: 6,
        background: '#fff',
      }}
    >
      <ControlButton
        onClick={() => void zoomIn()}
        title={zoomInLabel}
        aria-label={zoomInLabel}
      >
        <AddIcon fontSize="small" />
      </ControlButton>
      <ControlButton
        onClick={() => void zoomOut()}
        title={zoomOutLabel}
        aria-label={zoomOutLabel}
      >
        <RemoveIcon fontSize="small" />
      </ControlButton>
      <ControlButton
        onClick={() => void fitView(FIT_VIEW_OPTIONS)}
        title={fitViewLabel}
        aria-label={fitViewLabel}
      >
        <FitScreenIcon fontSize="small" />
      </ControlButton>
    </Controls>
  );
}

// ReactFlow removes pointer events from a node wrapper when a node is neither
// selectable nor draggable and has no mouse handlers. This intentional no-op
// keeps the graph read-only while allowing its tooltip and links to receive
// pointer events. Because the node remains non-draggable, ReactFlow does not add
// its `nopan` class, so dragging from the card still pans the canvas.
function enableReadOnlyNodePointerEvents() {}

function OrganizationFlowInner({
  roots,
  search = '',
  highlightedIds = [],
  selfLabel,
  statusLabels = {},
  ariaLabel,
  height = 520,
}: OrganizationFlowProps) {
  const { nodes, edges } = useMemo(() => {
    const flowNodes: Node<OrganizationNodeData>[] = [];
    const flowEdges: Edge[] = [];
    const explicitHighlights = new Set(highlightedIds);

    const place = (node: OrganizationFlowNode, left: number, depth: number): number => {
      const width = subtreeWidth(node);
      const x = left + width / 2 - NODE_WIDTH / 2;
      const highlighted = explicitHighlights.has(node.id) || matchesSearch(node, search);
      const renderedChildCount = node.children.length;
      const hiddenReportCount = Math.max(
        0,
        (node.directReportCount ?? renderedChildCount) - renderedChildCount,
      );
      const statusLabel =
        node.status && node.status !== 'active' && node.status !== 'invited'
          ? statusLabels[node.status]
          : undefined;

      flowNodes.push({
        id: node.id,
        type: 'organizationNode',
        position: { x, y: depth * VERTICAL_GAP },
        data: {
          ...node,
          highlighted,
          selfLabel,
          statusLabel,
          hiddenReportCount,
          // `place` is called with depth 0 for roots and depth+1 for children, so
          // depth is exactly "has a manager in this view". Edges below are pushed
          // once per child, so renderedChildCount is exactly "has a report here".
          hasParent: depth > 0,
          hasChildren: renderedChildCount > 0,
        },
        draggable: false,
        connectable: false,
        selectable: false,
        ariaLabel: [node.name, node.title, node.department].filter(Boolean).join(', '),
        style: { width: NODE_WIDTH },
      });

      let childLeft = left;
      for (const child of node.children) {
        flowEdges.push({
          id: `${node.id}-${child.id}`,
          source: node.id,
          target: child.id,
          type: 'smoothstep',
          markerEnd: {
            type: MarkerType.ArrowClosed,
            width: 12,
            height: 12,
            color: '#9e9e9e',
          },
          style: { stroke: '#9e9e9e', strokeWidth: 1.25 },
        });
        childLeft = place(child, childLeft, depth + 1) + HORIZONTAL_GAP;
      }
      return left + width;
    };

    let rootLeft = 0;
    for (const root of roots) {
      rootLeft = place(root, rootLeft, 0) + HORIZONTAL_GAP * 2;
    }
    return { nodes: flowNodes, edges: flowEdges };
  }, [highlightedIds, roots, search, selfLabel, statusLabels]);

  if (nodes.length === 0) return null;

  return (
    <Box
      sx={{
        width: '100%',
        height,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <ReactFlow
        aria-label={ariaLabel}
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        fitView
        fitViewOptions={FIT_VIEW_OPTIONS}
        nodesDraggable={false}
        nodesConnectable={false}
        nodesFocusable={false}
        edgesFocusable={false}
        connectOnClick={false}
        elementsSelectable={false}
        onNodeMouseEnter={enableReadOnlyNodePointerEvents}
        panOnDrag
        zoomOnScroll
        zoomOnPinch
        zoomOnDoubleClick={false}
        minZoom={0.25}
        maxZoom={1.8}
        proOptions={{ hideAttribution: true }}
        style={{ background: '#fafafa' }}
      >
        <Background variant={BackgroundVariant.Dots} gap={16} size={1} color="#e0e0e0" />
        <OrganizationFlowControls />
      </ReactFlow>
    </Box>
  );
}

export default function OrganizationFlow(props: OrganizationFlowProps) {
  return (
    <ReactFlowProvider>
      <OrganizationFlowInner {...props} />
    </ReactFlowProvider>
  );
}
